import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowDown2, ArrowUp2, Buildings } from "iconsax-react";
import cn from "classnames";
import { Button, Checkbox, DatePicker, Input, Prompt, SearchableSelect } from "@/shared/ui";
import { BANKS, type Bank } from "@/shared/consts/banks";
import { PROFILE_PAGE_ROUTE, extractErrorMessage } from "@/shared/utils";
import { useCreateDemoOrgMutation } from "../../api/demo";
import { DemoCredentialsScreen } from "../../components";
import { DEMO_SEED_INTENT_KEY } from "../../consts";
import { type CreateDemoOrgFormValues, createDemoOrgSchema } from "../../validation";

const ATTRIBUTION_PARAMS = ["campaign", "email", "ref", "utm_source", "utm_medium", "utm_campaign"];

interface PromptState {
  title: string;
  text: string;
  variant?: "success" | "error" | "warning";
}

export default function CreateDemoOrgPage() {
  const { t, i18n } = useTranslation("DemoPage");
  const locale = (i18n.language as "ru" | "kk" | "en") || "ru";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [createDemoOrg, { isLoading }] = useCreateDemoOrgMutation();
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [showOptional, setShowOptional] = useState(false);
  const [credentials, setCredentials] = useState<{ login: string; password: string } | null>(null);
  const [seedAfterContinue, setSeedAfterContinue] = useState(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateDemoOrgFormValues>({
    resolver: zodResolver(createDemoOrgSchema),
    defaultValues: { seed_mock_data: true },
  });

  function collectDemoSource(): Record<string, string> | undefined {
    const source: Record<string, string> = {};
    ATTRIBUTION_PARAMS.forEach(function readParam(param) {
      const value = searchParams.get(param);
      if (value) source[param] = value;
    });
    return Object.keys(source).length > 0 ? source : undefined;
  }

  async function onSubmit(data: CreateDemoOrgFormValues) {
    if (isLoading) return;
    try {
      const result = await createDemoOrg({
        organization_name: data.organization_name,
        bin: data.bin,
        chief_surname: data.chief_surname,
        chief_name: data.chief_name,
        chief_patronymic: data.chief_patronymic || undefined,
        chief_email: data.chief_email || undefined,
        chief_phone: data.chief_phone || undefined,
        address_ru: data.address_ru || undefined,
        address_kk: data.address_kk || undefined,
        bank_name: data.bank_name || undefined,
        iban: data.iban || undefined,
        bik: data.bik || undefined,
        registration_date: data.registration_date || undefined,
        initial_cash: data.initial_cash || undefined,
        seed_mock_data: false,
        demo_source: collectDemoSource(),
      }).unwrap();
      if (result.credentials) {
        setSeedAfterContinue(!!data.seed_mock_data);
        setCredentials(result.credentials);
      } else {
        if (data.seed_mock_data) {
          sessionStorage.setItem(DEMO_SEED_INTENT_KEY, "1");
        }
        navigate(PROFILE_PAGE_ROUTE, { replace: true });
      }
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.createError", t),
        variant: "error",
      });
    }
  }

  function handleBankChange(bank: Bank | null) {
    setSelectedBank(bank);
    if (bank) {
      setValue("bank_name", bank.name, { shouldValidate: true });
      setValue("bik", bank.bik, { shouldValidate: true });
    } else {
      setValue("bank_name", "", { shouldValidate: true });
      setValue("bik", "", { shouldValidate: true });
    }
  }

  function handleContinue() {
    if (seedAfterContinue) {
      sessionStorage.setItem(DEMO_SEED_INTENT_KEY, "1");
    }
    navigate(PROFILE_PAGE_ROUTE, { replace: true });
  }

  if (credentials) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <DemoCredentialsScreen credentials={credentials} onContinue={handleContinue} />
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <div className="flex-1 flex items-center justify-center p-5 pt-0">
        <div className="surface-base-fill radius-3xl p-7 w-full max-w-xl flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 radius-md surface-brand-subtle content-action-brand flex items-center justify-center shrink-0">
                <Buildings size={20} color="currentColor" />
              </div>
              <h1 className="text-display-2xs content-base-primary">{t("title")}</h1>
            </div>
            <p className="text-body-regular-sm content-base-secondary">{t("subtitle")}</p>
          </div>

          <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
            <Input
              label={t("fields.organizationName")}
              placeholder={t("placeholders.organizationName")}
              hasRequiredLabel
              error={errors.organization_name?.message ? t(errors.organization_name.message) : undefined}
              {...register("organization_name")}
            />
            <Input
              label={t("fields.bin")}
              placeholder={t("placeholders.bin")}
              hasRequiredLabel
              maxLength={12}
              error={errors.bin?.message ? t(errors.bin.message) : undefined}
              {...register("bin")}
            />
            <div className="flex flex-col gap-2">
              <span className="text-body-bold-sm content-base-primary">{t("fields.chiefFullName")}</span>
              <div className="grid grid-cols-3 gap-4">
                <Input
                  placeholder={t("placeholders.chiefSurname")}
                  hasRequiredLabel
                  error={errors.chief_surname?.message ? t(errors.chief_surname.message) : undefined}
                  {...register("chief_surname")}
                />
                <Input
                  placeholder={t("placeholders.chiefName")}
                  hasRequiredLabel
                  error={errors.chief_name?.message ? t(errors.chief_name.message) : undefined}
                  {...register("chief_name")}
                />
                <Input
                  placeholder={t("placeholders.chiefPatronymic")}
                  error={errors.chief_patronymic?.message ? t(errors.chief_patronymic.message) : undefined}
                  {...register("chief_patronymic")}
                />
              </div>
            </div>
            <Input
              label={t("fields.chiefEmail")}
              placeholder={t("placeholders.chiefEmail")}
              hasRequiredLabel
              error={errors.chief_email?.message ? t(errors.chief_email.message) : undefined}
              {...register("chief_email")}
            />

            <button
              type="button"
              className="flex items-center gap-2 content-action-brand text-body-bold-sm cursor-pointer outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-brand-200 radius-xs w-fit"
              onClick={() => setShowOptional(!showOptional)}>
              {showOptional ? <ArrowUp2 size={16} color="currentColor" /> : <ArrowDown2 size={16} color="currentColor" />}
              {t("optionalBlock.toggle")}
            </button>

            <div className={cn("flex-col gap-4", showOptional ? "flex" : "hidden")}>
              <Input
                label={t("fields.chiefPhone")}
                placeholder={t("placeholders.chiefPhone")}
                error={errors.chief_phone?.message ? t(errors.chief_phone.message) : undefined}
                {...register("chief_phone")}
              />
              <Input
                label={t("fields.addressRu")}
                placeholder={t("placeholders.addressRu")}
                error={errors.address_ru?.message ? t(errors.address_ru.message) : undefined}
                {...register("address_ru")}
              />
              <SearchableSelect<Bank>
                label={t("fields.bankName")}
                placeholder={t("placeholders.bankName")}
                searchPlaceholder={t("placeholders.bankSearchPlaceholder")}
                options={BANKS}
                value={selectedBank}
                onChange={handleBankChange}
                searchKeys={["name", "bik"]}
                displayKey="name"
                error={errors.bank_name?.message ? t(errors.bank_name.message) : undefined}
              />
              <Input
                label={t("fields.iban")}
                placeholder={t("placeholders.iban")}
                error={errors.iban?.message ? t(errors.iban.message) : undefined}
                {...register("iban")}
              />
              <div className="grid grid-cols-2 gap-4">
                <DatePicker
                  locale={locale}
                  mode="single"
                  label={t("fields.registrationDate")}
                  value={watch("registration_date") ? new Date(watch("registration_date") as string) : null}
                  onChange={(date) =>
                    setValue("registration_date", date ? date.toISOString().slice(0, 10) : "", {
                      shouldValidate: true,
                    })
                  }
                />
                <Input
                  label={t("fields.initialCash")}
                  placeholder="0"
                  error={errors.initial_cash?.message ? t(errors.initial_cash.message) : undefined}
                  {...register("initial_cash")}
                />
              </div>
            </div>

            <Checkbox
              state={watch("seed_mock_data") ? "checked" : "unchecked"}
              onChange={() => setValue("seed_mock_data", !watch("seed_mock_data"), { shouldValidate: true })}
              label={t("seedCheckbox")}
            />

            <Button variant="primary" size="huge" type="submit" disabled={isLoading}>
              {isLoading ? t("messages.loading") : t("buttons.submit")}
            </Button>
          </form>
        </div>
      </div>

      {isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.creating")}
          additionalText={t("messages.loader.pleaseWait")}
          namespace="DemoPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "error"}
          onClose={() => setPrompt(null)}
          namespace="DemoPage"
        />
      )}
    </>
  );
}
